import {
  PersonInfo,
  RequiredSubInfoPresenter,
  Subscriber,
  SubscriberErrorMessageType,
  SubscriberProductLineType,
} from 'types/electionService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types';
import { normalizeSubscriberClass } from './subscriberClassHelper';
import { getPLineProductType } from './productLineElection';

/**
 * Product-line validation helpers. They take {@link RequiredSubInfoPresenter} only — flow-specific
 * subscriber fields are resolved upstream via `createRequiredSubInfoPresenter`.
 */

/**
 * Mapping of product line types to their corresponding action types.
 * Used to determine which form configuration to use (elected vs waived).
 */
export const ProductLineTypeToActionType = Object.freeze({
  NOT_SELECTED: '',
  SELECTED: 'elected',
  WAIVED: 'waived',
});

export interface RequiredFieldsFilteringParams {
  productLineId: ProductLineId;
  productLineType: SubscriberProductLineType;
  employeeClass: string;
  carrierId: string;
  productId: string;
}

export interface FilteringParams extends RequiredFieldsFilteringParams {
  dependentOnProductLineType: SubscriberProductLineType | null;
}

export interface ProductLineRateCheck {
  showActionButton: boolean;
  errorMessageTypes: SubscriberErrorMessageType[];
}

export interface TotalRateCalculationResult {
  /**
   * List of invalid product lines.
   * This is an empty array if all product line specific fields are populated and valid.
   */
  incompleteProductLineIds: ProductLineId[];

  /**
   * List of not selected dependent product lines.
   * This is an empty array if all dependent product lines are selected or there are no dependent product lines.
   */
  productLineIdsMissingSelection: ProductLineId[];

  /**
   * List of error types.
   * This is an empty array if all product line specific fields and high level fields are valid.
   */
  errorTypes: SubscriberErrorMessageType[];
}

/**
 * Checks that all given field keys are truthy on subscriber common info.
 */
export const checkIfFormFieldsArePopulated = (fields: string[], subscriberCommonInfo: PersonInfo): boolean => {
  return fields.every((field) => subscriberCommonInfo[field]);
};

/**
 * Resolves required fields for a specific employee class (or default).
 */
export const getClassRequiredFields = (
  classRequiredFieldsMap: Record<string, string[]>,
  employeeClass: string,
): string[] => {
  const classRequiredFieldsKey =
    Object.keys(classRequiredFieldsMap).find(
      (eeClass) => normalizeSubscriberClass(eeClass) === normalizeSubscriberClass(employeeClass),
    ) || '';

  const classRequiredFields = classRequiredFieldsMap[classRequiredFieldsKey] || [];

  if (classRequiredFields.length > 0) {
    return classRequiredFields;
  }

  return [];
};

/**
 * Resolves required fields for a specific carrier (or default).
 */
export const getCarrierRequiredFields = (
  carrierRequiredFieldsMap: Record<string, string[]>,
  carrierId: string,
): string[] => {
  const carrierRequiredFields = carrierRequiredFieldsMap[carrierId] || [];

  if (carrierRequiredFields.length > 0) {
    return carrierRequiredFields;
  }

  return [];
};

/**
 * Resolves required fields for a specific product (or default).
 */
export const getProductRequiredFields = (
  productRequiredFieldsMap: Record<string, string[]>,
  productId: string,
): string[] => {
  const productRequiredFields = productRequiredFieldsMap[productId] || [];

  if (productRequiredFields.length > 0) {
    return productRequiredFields;
  }

  return [];
};

/**
 * Collects all required fields for a product line (base + class + carrier + product)
 * based on product line type (elected/waived), employee class, carrier ID, and product ID.
 */
export const getProductLineRequiredFields = (
  params: RequiredFieldsFilteringParams,
  requiredSubInfo?: RequiredSubInfoPresenter,
): string[] => {
  if (!requiredSubInfo || params.productLineType === 'NOT_SELECTED') {
    return [];
  }

  const productLineInfo = requiredSubInfo.productLineRequiredInfo?.[params.productLineId];
  if (!productLineInfo) {
    return [];
  }

  const actionType = ProductLineTypeToActionType[params.productLineType];
  const requiredFieldsSet = productLineInfo[actionType];

  const basicProductLineRequiredFields = productLineInfo[actionType]?.requiredFields || [];

  const classRequiredFields = getClassRequiredFields(
    requiredFieldsSet?.classRequiredFields || {},
    params.employeeClass,
  );

  const carrierRequiredFields = getCarrierRequiredFields(
    requiredFieldsSet?.carrierRequiredFields || {},
    params.carrierId,
  );

  const productRequiredFields = getProductRequiredFields(
    requiredFieldsSet?.productRequiredFields || {},
    params.productId,
  );

  return [
    ...basicProductLineRequiredFields,
    ...classRequiredFields,
    ...carrierRequiredFields,
    ...productRequiredFields,
  ];
};

/**
 * Get form ID for a product line based on action type.
 * Note: not selected product lines don't have a RequiredFields map, therefore formId will be empty string
 */
export const getFormIdForProductLine = (
  productLineId: ProductLineId,
  productLineType: SubscriberProductLineType,
  requiredSubInfo?: RequiredSubInfoPresenter,
): string => {
  if (!requiredSubInfo || productLineType === 'NOT_SELECTED') {
    return '';
  }

  const actionType = productLineType === 'SELECTED' ? 'elected' : 'waived';
  const productLineInfo = requiredSubInfo.productLineRequiredInfo?.[productLineId];
  return productLineInfo?.[actionType]?.formId || '';
};

/**
 * Validates a single product line rate and returns error types and rate button visibility.
 */
export const checkProductLineRateValidity = (
  subscriberCommonInfo: PersonInfo,
  filteringParams: FilteringParams,
  requiredSubInfo?: RequiredSubInfoPresenter,
): ProductLineRateCheck => {
  const requiredFields = getProductLineRequiredFields(filteringParams, requiredSubInfo);
  const errorMessageTypes: SubscriberErrorMessageType[] = [];

  if (filteringParams.productLineType === 'NOT_SELECTED') {
    return {
      errorMessageTypes: [],
      showActionButton: false,
    };
  }

  if (
    filteringParams.productLineType === 'SELECTED' &&
    filteringParams.dependentOnProductLineType !== null &&
    filteringParams.dependentOnProductLineType !== 'SELECTED'
  ) {
    errorMessageTypes.push('MISSING_DEPENDENT_PRODUCT_LINE_SELECTION');
  }

  if (!checkIfFormFieldsArePopulated(requiredFields, subscriberCommonInfo)) {
    errorMessageTypes.push('MISSING_PRODUCT_LINE_ANSWERS');
  }
  return {
    errorMessageTypes,
    showActionButton: requiredFields.length > 0,
  };
};

/**
 * Retrieves additional form IDs based on subscriber choices and required sub info.
 * Returns an array of form IDs that should be fetched to display additional subscriber information.
 *
 * @param subscriber - The subscriber object containing choices
 * @param requiredSubInfo - The required subscriber info presenter containing product line form mappings
 * @returns Array of form IDs to fetch
 */
export const getAdditionalFormIdsFromSubscriberChoices = (
  subscriber: Subscriber,
  requiredSubInfoPresenter: RequiredSubInfoPresenter | undefined,
): string[] => {
  const additionalFormIds: string[] = [];
  const productLineRequiredInfo = requiredSubInfoPresenter?.productLineRequiredInfo;

  if (!productLineRequiredInfo) {
    return additionalFormIds;
  }

  subscriber.choice.forEach((choice) => {
    if (!choice.productLineId) return;

    const productLineType = getPLineProductType(choice.productId);

    const carrierId = subscriber.productLineToCarrier[choice.productLineId] || '';
    const productLineRequiredFields: string[] = getProductLineRequiredFields(
      {
        productLineId: choice.productLineId,
        productLineType,
        employeeClass: subscriber.employeeClass,
        carrierId,
        productId: choice.productId,
      },
      requiredSubInfoPresenter,
    );

    const formId = getFormIdForProductLine(choice.productLineId, productLineType, requiredSubInfoPresenter);

    // FormId may be present but there are no required fields for specific product line choice / class / carrier
    if (formId && productLineRequiredFields.length > 0) {
      additionalFormIds.push(formId);
    }
  });

  return additionalFormIds;
};
